<template>
    <div />
</template>

<script setup lang="ts">
import {computed, onMounted} from 'vue'
import { useHead, useSeoMeta, useNuxtApp } from '#imports'
import type { HblProduct } from '@/utils/types'
import { normalizeSlashes } from '@/utils/helper'
import { useI18n } from 'vue-i18n'

const { t } = useI18n()

const props = defineProps<{
    data: HblProduct
}>()

const route = useRoute()
const config = useRuntimeConfig().public
const { isLocalisedRoute } = useLocalisation()
const routeLocale = isLocalisedRoute(route.path)
const productIndexPage = ref(props.data?.customFields?.noindex)

// Use the product's URL from parentSeoUrls (mapped in hblMapProduct)
// This ensures canonical URLs always use parent product URLs from extensions.parentSeoUrls
let canonicalUrl = config?.appBaseUrl + (props.data?.url || route?.path)

if (routeLocale) {
    canonicalUrl = config?.appBaseUrl + routeLocale
}

canonicalUrl = normalizeSlashes(canonicalUrl)

const selectedMedia = computed(() => {
    if (props.data?.media?.length >= 2) {
        // @ts-ignore
        return props.data?.media[1]
    } else if (props.data?.media?.length === 1) {
        // @ts-ignore
        return props.data?.media[0]
    } else {
        return null
    }
})

function removeHtmlTags(input: string): string {
    return input?.replace(/<[^>]*>/g, '');
}

const priceValidUntilDate = computed(() => {
    const today = new Date()
    const nextYear = new Date(today)
    nextYear.setFullYear(today.getFullYear() + 1)
    return nextYear.toISOString().split('T')[0] // Format als YYYY-MM-DD
})

onMounted(async () => {
    if(route?.query?.epoqId) {
        const { reportClickThrough } = useEpoq()
        await reportClickThrough(route.query.epoqId as string, props.data?.sku)
    }
})

function decodeHtmlEntities(str: string = ''): string {
    if (import.meta.client) {
        const txt = document.createElement('textarea')
        txt.innerHTML = str
        return txt.value
    }
    return str
}

const webPageSchema = {
    "@context": "https://schema.org",
    "@type": "WebPage",
    "copyrightYear": "2014",
    "isFamilyFriendly": 'https://schema.org/True',
    "image": [
        selectedMedia.value.url
    ],
    "name": (props.data?.metaTitle ?? props.data?.name) + ' | ' + t('page.title'),
    "description": removeHtmlTags(props.data?.description),
    "copyrightHolder": {
        "@type": "Thing",
        "name": t('page.title'),
    }
}

const productSchema = {
    "@context": "https://schema.org",
    "@type": "Product",
    "sku": props.data?.sku,
    "name": props.data?.metaTitle ?? props.data?.name,
    "image": [
        selectedMedia.value.url
    ],
    "description": props.data?.metaDescription ?? removeHtmlTags(props.data?.description),
    "mpn": props.data?.sku,
    "weight": props.data?.weight ? (props.data?.weight + ' ' + props.data?.unit?.translated?.name) : null,
    "url": canonicalUrl,
    "brand": {
        "@type": "Brand",
        "name": props.data?.customFields?.brand_name ?? decodeHtmlEntities(props.data?.manufacturer?.description ?? props.data?.manufacturer?.name),
        "logo": props.data?.manufacturer?.media?.url
    },
    "offers": {
        "@type": "Offer",
        "priceCurrency": "EUR",
        "priceValidUntil": priceValidUntilDate.value,
        "price": props.data?.price?.specialPrice ?? props.data?.price?.regularPrice,
        "availability": props.data?.deliveryTimeCustomFields?.available_for_purchase === 'available_for_purchase_orderable' ? 'https://schema.org/InStock' : 'https://schema.org/OutOfStock',
        "itemCondition": "https://schema.org/NewCondition"
    }
}

// max-image-preview:large comes from the module's robotsEnabledValue; useRobotsRule keeps a single robots meta tag
useRobotsRule(!productIndexPage.value)

const ogTitle = computed(() => props.data?.metaTitle ?? props.data?.name)
const ogImage = computed(() => selectedMedia.value?.url)

useSeoMeta({
    description: () => props.data?.metaDescription,
    ogType: 'website',
    ogSiteName: ogTitle,
    ogTitle,
    ogDescription: () => props.data?.metaDescription,
    ogUrl: canonicalUrl,
    ogImage,
    ogImageAlt: ogTitle
})

useHead({
    title: (props.data?.metaTitle ?? props.data?.name) + ' | ' + t('page.title'),
    link: [
        {
            rel: 'canonical',
            href: canonicalUrl
        }
    ],
    script: [
        {
            type: 'application/ld+json',
            innerHTML: JSON.stringify(webPageSchema),
        },
        {
            type: 'application/ld+json',
            innerHTML: JSON.stringify(productSchema),
        }
    ]
})

const { $hblBus } = useNuxtApp()
onMounted(() => {
    $hblBus.$emit('viewProductDetail', { product: props.data })
})
</script>
